import React from "react";
import {
  Typography,
  Divider,
  Row,
  Col,
  InputNumber,
  Select,
  DatePicker,
  Switch,
  TimePicker,
} from "antd";

const { Title, Text } = Typography;

const CURRENCIES = ["NGN", "USD", "GBP", "EUR"];

const Label = ({ children, required, error }) => (
  <div style={{ marginBottom: 8, fontWeight: 500 }}>
    {children}
    {required ? " *" : ""}
    {error && (
      <span style={{ color: "#ff4d4f", fontWeight: 400 }}> — {error}</span>
    )}
  </div>
);

/**
 * Step 4 — Pricing: base price, dates, and the fields that diverge between
 * the two list types (occupancy/verified for Packages, group size/duration/
 * schedule for Quick Trips). Customization pricing is entered in the
 * Components step, not here.
 */
const PricingStep = ({ form, setForm, errors = {} }) => {
  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updatePrice = (patch) =>
    setForm((prev) => ({ ...prev, price: { ...prev.price, ...patch } }));
  const updateOccupancy = (patch) =>
    setForm((prev) => ({ ...prev, occupancy: { ...prev.occupancy, ...patch } }));
  const updateSchedule = (patch) =>
    setForm((prev) => ({ ...prev, schedule: { ...prev.schedule, ...patch } }));

  const isQuickTrip = form.listType === "QUICK_TRIP";

  return (
    <div>
      <Title level={4}>Pricing</Title>
      <Text type="secondary">
        Base price, availability window, and{" "}
        {isQuickTrip ? "group size / schedule" : "occupancy"}.
      </Text>

      <Divider />

      <Row gutter={16}>
        <Col span={12}>
          <Label required error={errors.priceAmount}>Price amount</Label>
          <InputNumber
            style={{ width: "100%" }}
            status={errors.priceAmount ? "error" : undefined}
            min={0}
            value={form.price.amount}
            onChange={(amount) => updatePrice({ amount })}
          />
        </Col>
        <Col span={12}>
          <Label>Currency</Label>
          <Select
            style={{ width: "100%" }}
            value={form.price.currency}
            onChange={(currency) => updatePrice({ currency })}
            options={CURRENCIES.map((c) => ({ value: c, label: c }))}
          />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Label required error={errors.startDate}>Start date</Label>
          <DatePicker
            style={{ width: "100%" }}
            status={errors.startDate ? "error" : undefined}
            value={form.startDate}
            onChange={(startDate) => update({ startDate })}
          />
        </Col>
        <Col span={12}>
          <Label required error={errors.endDate}>End date</Label>
          <DatePicker
            style={{ width: "100%" }}
            status={errors.endDate ? "error" : undefined}
            value={form.endDate}
            onChange={(endDate) => update({ endDate })}
          />
        </Col>
      </Row>

      {isQuickTrip ? (
        <>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Label required error={errors.duration}>Duration (days)</Label>
              <InputNumber
                style={{ width: "100%" }}
                status={errors.duration ? "error" : undefined}
                min={1}
                value={form.duration}
                onChange={(duration) => update({ duration })}
              />
            </Col>
            <Col span={12}>
              <Label required error={errors.groupSize}>Group size</Label>
              <InputNumber
                style={{ width: "100%" }}
                status={errors.groupSize ? "error" : undefined}
                min={1}
                value={form.groupSize}
                onChange={(groupSize) => update({ groupSize })}
              />
            </Col>
          </Row>

          <Title level={5} style={{ marginTop: 24 }}>
            Schedule
          </Title>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <Switch
                checked={form.schedule.runs_daily}
                onChange={(runs_daily) => updateSchedule({ runs_daily })}
                checkedChildren="Runs daily"
                unCheckedChildren="Not daily"
              />
            </Col>
            <Col span={6}>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                addonAfter="hrs lead"
                value={form.schedule.min_booking_lead_hours}
                onChange={(min_booking_lead_hours) =>
                  updateSchedule({ min_booking_lead_hours })
                }
              />
            </Col>
            <Col span={6}>
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                placeholder="Pickup time"
                value={form.schedule.pickup_time}
                onChange={(pickup_time) => updateSchedule({ pickup_time })}
              />
            </Col>
            <Col span={6}>
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                placeholder="Return time"
                value={form.schedule.return_time}
                onChange={(return_time) => updateSchedule({ return_time })}
              />
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Label required error={errors.travellersPerRoom}>
                Travelers per room
              </Label>
              <InputNumber
                style={{ width: "100%" }}
                status={errors.travellersPerRoom ? "error" : undefined}
                min={1}
                value={form.occupancy.travellers_per_room}
                onChange={(travellers_per_room) =>
                  updateOccupancy({ travellers_per_room })
                }
              />
            </Col>
            <Col span={12}>
              <Label required error={errors.maxTravellers}>
                Max travelers per booking
              </Label>
              <InputNumber
                style={{ width: "100%" }}
                status={errors.maxTravellers ? "error" : undefined}
                min={1}
                value={form.occupancy.max_travellers_per_booking}
                onChange={(max_travellers_per_booking) =>
                  updateOccupancy({ max_travellers_per_booking })
                }
              />
            </Col>
          </Row>

          <Row
            align="middle"
            justify="space-between"
            style={{
              marginTop: 24,
              padding: 16,
              border: "1px solid #f0f0f0",
              borderRadius: 8,
            }}
          >
            <Col>
              <div style={{ fontWeight: 500 }}>Verified</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Has no server default — always set explicitly.
              </Text>
            </Col>
            <Col>
              <Switch
                checked={form.verified}
                onChange={(verified) => update({ verified })}
              />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default PricingStep;
